import React from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box, Checkbox, Chip, Typography } from '@material-ui/core';
import clsx from 'clsx';
import StatusBadge from './StatusBadge';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '18%',
      borderRight: '1px solid #ccc',
      overflowY: 'auto',
      margin: 0,
      padding: 0,
      position: 'relative',
      height: '100%',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px',
      borderBottom: '1px solid #ccc',
      position: 'sticky',
      top: 0,
      backgroundColor: '#fff',
      zIndex: 1,
    },
    h3: { margin: 0 },
    ul: { listStyle: 'none', padding: 0, margin: 0 },
    li: {
      padding: '10px',
      cursor: 'pointer',
      borderBottom: '1px solid #eee',
      display: 'flex',
      alignItems: 'center',
      '&:hover': { backgroundColor: '#f9f9f9' },
    },
    liSelected: { backgroundColor: '#ddd' },
    name: { marginLeft: theme.spacing(1) },
    nameRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginLeft: theme.spacing(1),
      minWidth: 0,
    },
    badge: {
      marginLeft: theme.spacing(1),
    },
    dncChip: {
      backgroundColor: '#f44336',
      color: '#fff',
      height: 20,
      fontWeight: 600,
    },
  })
);

export type LeadListProps = {
  leadIds: string[];
  isSelected: (id: string) => boolean;
  isChecked: (id: string) => boolean;
  onSelect: (id: string) => void;
  onToggle: (id: string, checked: boolean) => void;
  getLabel: (id: string) => string;
  getDoNotCall?: (id: string) => boolean;
  title?: string;

  activeLeadId?: string;
  activeStatus?:
    | 'registering'
    | 'registered'
    | 'calling'
    | 'connected'
    | 'ended'
    | 'failed';
};

const LeadList: React.FC<LeadListProps> = ({
  leadIds,
  isSelected,
  isChecked,
  onSelect,
  onToggle,
  getLabel,
  getDoNotCall,
  title = 'Leads',
  activeLeadId,
  activeStatus,
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.container} role="navigation" aria-label="Lead list">
      <Box className={classes.header}>
        <h3 className={classes.h3}>{title}</h3>
      </Box>
      <ul className={classes.ul}>
        {leadIds.map(id => {
          const selected = isSelected(id);
          const checked = isChecked(id);
          const label = getLabel(id);
          const isDNC = getDoNotCall?.(id) ?? false;

          return (
            <li
              key={id}
              className={clsx(classes.li, { [classes.liSelected]: selected })}
              onClick={() => onSelect(id)}
              role="button"
              aria-pressed={selected}
              aria-label={`Lead ${label}${isDNC ? ' (Do Not Call)' : ''}`}
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') onSelect(id);
              }}
            >
              <div className={classes.nameRow}>
                <Typography className={classes.name} noWrap>
                  {label}
                </Typography>

                {isDNC && (
                  <Chip
                    className={classes.dncChip}
                    label="DO NOT CALL"
                    size="small"
                    aria-label="Do Not Call"
                  />
                )}

                {(() => {
                  const isActiveSelected = selected && activeLeadId === id;
                  const shouldShow =
                    activeStatus === 'calling' ||
                    activeStatus === 'connected' ||
                    activeStatus === 'ended';
                  if (!isActiveSelected || !shouldShow) return null;

                  return (
                    <StatusBadge
                      status={activeStatus}
                      className={classes.badge}
                    />
                  );
                })()}
              </div>
            </li>
          );
        })}
      </ul>
    </Box>
  );
};

export default LeadList;
